import { useRef } from 'react';
import AutoSizer from 'react-virtualized-auto-sizer';
import { FCX } from '@datassential/platform-ui-lib';
import HomeSectionHeader from '@pages/Home/components/HomeSectionHeader';
import HomeSectionLoader from '@pages/Home/components/HomeSectionLoader';
import HomeSectionTitle from '@pages/Home/components/HomeSectionTitle';
import TerritoryScopeLabel from '@pages/Home/components/TerritoryScopeLabel/TerritoryScopeLabel';
import { OpeningsClosingsFilterProvider, useOpeningsClosingsFilters } from './context/OpeningsClosingsFilterContext';
import OpeningsClosingsFilters from './OpeningsClosingsFilters';
import OpeningsClosingsExportButton from './OpeningsClosingsExportButton';
import OpeningsClosingsLegend from './OpeningsClosingsLegend';
import OpeningsClosingsChart from './OpeningsClosingsChart';
import './RestaurantOpeningsClosings.scss';

const CHART_HEIGHT = 260;

const OpeningsClosingsBody = () => {
    const { data, isLoading, isSuccess } = useOpeningsClosingsFilters();

    // React "previous render value" idiom — mutating a ref during render is correct here
    // (no setState, no side-effect). A useEffect would introduce a one-render lag, causing
    // the old chart to flash before the new key takes effect.
    const chartKeyRef = useRef(0);
    const prevDataRef = useRef(data);
    if (prevDataRef.current !== data) {
        prevDataRef.current = data;
        chartKeyRef.current += 1;
    }

    if (isLoading) return <HomeSectionLoader />;

    if (isSuccess === false) {
        return (
            <div className="RestaurantOpeningsClosings__empty">
                Openings &amp; closings data couldn't be loaded. Refresh the page to try again.
            </div>
        );
    }

    return (
        <div className="RestaurantOpeningsClosings__body">
            <OpeningsClosingsLegend />
            <div className="RestaurantOpeningsClosings__chart">
                <AutoSizer disableHeight>
                    {({ width }) =>
                        width > 0 ? (
                            <OpeningsClosingsChart
                                key={chartKeyRef.current}
                                data={data}
                                width={width}
                                height={CHART_HEIGHT}
                            />
                        ) : null
                    }
                </AutoSizer>
            </div>
        </div>
    );
};

const RestaurantOpeningsClosingsContent = () => (
    <div className="RestaurantOpeningsClosings">
        <HomeSectionHeader>
            <div className="RestaurantOpeningsClosings__header">
                <HomeSectionTitle testId="textRestaurantOpeningsClosingsTitle">
                    Restaurant Openings &amp; Closings: <TerritoryScopeLabel />
                </HomeSectionTitle>
                <div className="RestaurantOpeningsClosings__controls">
                    <OpeningsClosingsFilters />
                    <OpeningsClosingsExportButton />
                </div>
            </div>
        </HomeSectionHeader>

        <OpeningsClosingsBody />
    </div>
);

const RestaurantOpeningsClosings: FCX = () => {
    return (
        <div className="Home__wrapper">
            <OpeningsClosingsFilterProvider>
                <RestaurantOpeningsClosingsContent />
            </OpeningsClosingsFilterProvider>
        </div>
    );
};

export default RestaurantOpeningsClosings;
